import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  DEFAULT_WORKSPACE_ID,
  WORKSPACE_STORAGE_KEY,
  WORKSPACES,
  findWorkspaceIdForPath,
  getWorkspaceById,
  type WorkspaceDefinition,
  type WorkspaceId,
  type WorkspaceNavGroup,
  type WorkspaceNavItem,
} from '../constants/workspaces';
import { useAuth } from './AuthProvider';

interface WorkspaceContextType {
  workspaceId: WorkspaceId;
  workspace: WorkspaceDefinition;
  workspaces: WorkspaceDefinition[];
  pinnedAboveItems: WorkspaceNavItem[];
  overviewItem: WorkspaceNavItem | null;
  visibleGroups: WorkspaceNavGroup[];
  setWorkspaceId: (id: WorkspaceId, options?: { navigateHome?: boolean }) => void;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

function readStoredWorkspaceId(): WorkspaceId {
  try {
    const stored = localStorage.getItem(WORKSPACE_STORAGE_KEY);
    if (stored && WORKSPACES.some((workspace) => workspace.id === stored)) {
      return stored as WorkspaceId;
    }
  } catch {
    // ignore storage errors
  }
  return DEFAULT_WORKSPACE_ID;
}

function filterGroupsByPermission(
  groups: WorkspaceNavGroup[],
  hasPermission: (permission: string) => boolean,
): WorkspaceNavGroup[] {
  return groups
    .map((group) => {
      const dashboard =
        group.dashboard &&
        (!group.dashboard.requiredPermission ||
          hasPermission(group.dashboard.requiredPermission))
          ? group.dashboard
          : undefined;
      const items = group.items.filter(
        (item: WorkspaceNavItem) =>
          !item.requiredPermission || hasPermission(item.requiredPermission),
      );
      return {
        ...group,
        dashboard,
        items,
      };
    })
    .filter((group) => Boolean(group.dashboard) || group.items.length > 0);
}

/** Prefer overview, then the first permitted nav item — never a hard-coded home the user can't open. */
function getWorkspaceLandingPath(
  workspace: WorkspaceDefinition,
  hasPermission: (permission: string) => boolean,
): string {
  const overview = workspace.overview;
  if (!overview.requiredPermission || hasPermission(overview.requiredPermission)) {
    return overview.path;
  }
  for (const group of workspace.groups) {
    if (
      group.dashboard &&
      (!group.dashboard.requiredPermission ||
        hasPermission(group.dashboard.requiredPermission))
    ) {
      return group.dashboard.path;
    }
    for (const item of group.items) {
      if (!item.requiredPermission || hasPermission(item.requiredPermission)) {
        return item.path;
      }
    }
  }
  return workspace.homePath;
}

export const WorkspaceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [workspaceId, setWorkspaceIdState] = useState<WorkspaceId>(readStoredWorkspaceId);
  const [hasManualSelection, setHasManualSelection] = useState(false);

  useEffect(() => {
    if (hasManualSelection) return;
    const matched = findWorkspaceIdForPath(location.pathname);
    if (matched && matched !== workspaceId) {
      setWorkspaceIdState(matched);
    }
  }, [hasManualSelection, location.pathname, workspaceId]);

  useEffect(() => {
    try {
      localStorage.setItem(WORKSPACE_STORAGE_KEY, workspaceId);
    } catch {
      // ignore storage errors
    }
  }, [workspaceId]);

  const setWorkspaceId = useCallback(
    (id: WorkspaceId, options?: { navigateHome?: boolean }) => {
      setHasManualSelection(true);
      setWorkspaceIdState(id);
      if (options?.navigateHome) {
        navigate(getWorkspaceLandingPath(getWorkspaceById(id), hasPermission));
      }
    },
    [hasPermission, navigate],
  );

  const workspace = useMemo(() => getWorkspaceById(workspaceId), [workspaceId]);

  const pinnedAboveItems = useMemo(() => {
    return (workspace.pinnedAbove ?? []).filter(
      (item) => !item.requiredPermission || hasPermission(item.requiredPermission),
    );
  }, [workspace.pinnedAbove, hasPermission]);

  const overviewItem = useMemo(() => {
    const item = workspace.overview;
    if (item.requiredPermission && !hasPermission(item.requiredPermission)) {
      return null;
    }
    return item;
  }, [workspace.overview, hasPermission]);

  const visibleGroups = useMemo(
    () => filterGroupsByPermission(workspace.groups, hasPermission),
    [workspace.groups, hasPermission],
  );

  const value = useMemo(
    () => ({
      workspaceId,
      workspace,
      workspaces: WORKSPACES,
      pinnedAboveItems,
      overviewItem,
      visibleGroups,
      setWorkspaceId,
    }),
    [workspaceId, workspace, pinnedAboveItems, overviewItem, visibleGroups, setWorkspaceId],
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = (): WorkspaceContextType => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within WorkspaceProvider');
  }
  return context;
};
